import { Icon } from '@iconify/react/dist/iconify.js';
import { useState } from 'react';

const CategoryDetails = ({ selectedCategory, selectedChild, copiedId, onCopy, onChildSelect, onEditCategory }) => {
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [isEquipmentCollapsed, setIsEquipmentCollapsed] = useState(false);
    
    const handleDropdownClick = (dropdownType) => {
        if (activeDropdown === dropdownType) {
            setActiveDropdown(null);
        } else {
            setActiveDropdown(dropdownType);
        }
    };

    const handleEquipmentToggle = () => {
        setIsEquipmentCollapsed(!isEquipmentCollapsed);
    };

    const handleChildClick = (child) => {
        onChildSelect(child);
    };
    
    if (!selectedCategory) return null;

    const toTitleCase = (str) => {
        return str.replace(
            /\w\S*/g,
            (text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase()
        );
    }

    return (
        <div className={`
            category-details-responsive overflow-y-auto scrollable transition-all duration-500 ease-out
            w-full gap-2 flex flex-col
            opacity-100 translate-x-0
            ${selectedCategory && !selectedChild ? '2xl:flex 2xl:w-3/5 bg-background details-container rounded-lg p-4' : selectedChild ? 'px-3' : ''}
        `}>
            <div className="flex flex-col md:flex-row gap-2 md:gap-0 justify-between items-start md:items-center">
                <div className="flex flex-row items-center gap-2">
                    {/* <Icon icon="duo-icons:box-2" className={`text-3xl ${
                        selectedCategory._hasPendingChanges ? 'text-orange-500 opacity-100' :
                        selectedCategory._isPendingCreation ? 'text-primary opacity-100' :
                        'text-accent opacity-30'
                    }`} /> */}
                    <div>
                        <h2 className="text-xl font-semibold text-font-primary">
                            {selectedCategory.parentCategory || 'Unnamed Category'}
                        </h2>
                        {selectedCategory._hasPendingChanges && (
                            <div className="text-xs text-orange-600 mt-1">
                                Modified by {selectedCategory._changedBy} • {new Date(selectedCategory._changedAt).toLocaleDateString()}
                            </div>
                        )}
                        {selectedCategory._isPendingCreation && (
                            <div className="text-xs text-blue-600 mt-1">
                                Created by {selectedCategory._changedBy} • {new Date(selectedCategory._changedAt).toLocaleDateString()}
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex flex-row items-center gap-2">
                    <div className="flex flex-row items-center gap-2 sm:gap-3 text-font-secondary">
                        <p className="text-xs sm:text-sm">Id: {selectedCategory.parentEqpId}</p>
                        <button
                            className="text-xs sm:text-sm button-container text-accent p-1.5 sm:p-2 rounded"
                            onClick={() => onCopy(selectedCategory.parentEqpId)}
                        >
                            <Icon
                                icon={
                                    copiedId === selectedCategory.parentEqpId
                                        ? 'mdi:check-bold'
                                        : 'solar:copy-linear'
                                }
                                className="text-sm sm:text-lg"
                            />
                        </button>
                    </div>
                    <button 
                        onClick={() => onEditCategory(selectedCategory)}
                        className="flex flex-row items-center rounded-md gap-1 text-xs sm:text-sm button-container p-1.5 sm:p-2 group hover:bg-accent transition-colors duration-300">
                        <Icon icon="fluent:box-edit-20-regular" className="text-sm sm:text-lg text-accent group-hover:text-background" />
                        <span className="text-sm font-medium text-font-primary group-hover:text-background">Edit</span>
                    </button>
                </div>
            </div>

            <div className="category-tabs-responsive grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {/* Category Summary */}
                <div 
                    className={`flex flex-row justify-between items-center gap-2 text-sm sm:text-base py-2 md:px-3 cursor-pointer transition-colors border-b border-bgAccent ${
                        activeDropdown === 'defaults' ? 'border-b-2 border-primary' : 'hover:bg-background'
                    }`}
                    onClick={() => handleDropdownClick('defaults')}
                >
                    <span className="text-font-primary whitespace-nowrap truncate">Defaults</span>
                    <Icon 
                        icon={activeDropdown === 'defaults' ? 'mdi:chevron-up' : 'mdi:chevron-down'} 
                        className="text-lg sm:text-2xl text-primary" 
                    />
                </div>
                <div 
                    className={`flex flex-row justify-between items-center gap-2 text-sm sm:text-base py-2 md:px-3 cursor-pointer transition-colors border-b border-bgAccent ${
                        activeDropdown === 'typicalUses' ? 'border-b-2 border-primary' : 'hover:bg-background'
                    }`}
                    onClick={() => handleDropdownClick('typicalUses')}
                >
                    <span className="text-font-primary whitespace-nowrap truncate">Typical Uses</span>
                    <Icon 
                        icon={activeDropdown === 'typicalUses' ? 'mdi:chevron-up' : 'mdi:chevron-down'} 
                        className="text-lg sm:text-2xl text-primary" 
                    />
                </div>
                <div 
                    className={`flex flex-row justify-between items-center gap-2 text-sm sm:text-base py-2 md:px-3 cursor-pointer transition-colors border-b border-bgAccent ${
                        activeDropdown === 'typicalFlow' ? 'border-b-2 border-primary' : 'hover:bg-background'
                    }`}
                    onClick={() => handleDropdownClick('typicalFlow')}
                >
                    <span className="text-font-primary whitespace-nowrap truncate">Typical Flow</span>
                    <Icon 
                        icon={activeDropdown === 'typicalFlow' ? 'mdi:chevron-up' : 'mdi:chevron-down'} 
                        className="text-lg sm:text-2xl text-primary" 
                    />
                </div>
                <div 
                    className={`flex flex-row justify-between items-center gap-2 text-sm sm:text-base py-2 md:px-3 cursor-pointer transition-colors border-b border-bgAccent ${
                        activeDropdown === 'qualifyingQuestions' ? 'border-b-2 border-primary' : 'hover:bg-background'
                    }`}
                    onClick={() => handleDropdownClick('qualifyingQuestions')}
                >
                    <span className="text-font-primary whitespace-nowrap truncate">Qualifying Questions</span>
                    <Icon 
                        icon={activeDropdown === 'qualifyingQuestions' ? 'mdi:chevron-up' : 'mdi:chevron-down'} 
                        className="text-lg sm:text-2xl text-primary" 
                    />
                </div>
                <div 
                    className={`flex flex-row justify-between items-center gap-2 text-sm sm:text-base py-2 md:px-3 cursor-pointer transition-colors border-b border-bgAccent ${
                        activeDropdown === 'specialNotes' ? 'border-b-2 border-primary' : 'hover:bg-background'
                    }`}
                    onClick={() => handleDropdownClick('specialNotes')}
                >
                    <span className="text-font-primary whitespace-nowrap truncate">Special Notes</span>
                    <Icon 
                        icon={activeDropdown === 'specialNotes' ? 'mdi:chevron-up' : 'mdi:chevron-down'} 
                        className="text-lg sm:text-2xl text-primary" 
                    />
                </div>
                <div 
                    className={`flex flex-row justify-between items-center gap-2 text-sm sm:text-base py-2 md:px-3 cursor-pointer transition-colors border-b border-bgAccent ${
                        activeDropdown === 'pricingConsiderations' ? 'border-b-2 border-primary' : 'hover:bg-background'
                    }`}
                    onClick={() => handleDropdownClick('pricingConsiderations')}
                >
                    <span className="text-font-primary whitespace-nowrap truncate">Pricing Considerations</span>
                    <Icon 
                        icon={activeDropdown === 'pricingConsiderations' ? 'mdi:chevron-up' : 'mdi:chevron-down'} 
                        className="text-lg sm:text-2xl text-primary" 
                    />
                </div>
            </div>

            {/* Dropdown Content */}
            {activeDropdown && (
                <div className="w-full rounded-md mt-2">
                    {activeDropdown === 'defaults' && (
                        <div className="flex flex-col gap-2 card-container rounded-md p-3">
                            <h3 className="font-semibold text-secondary">Defaults</h3>
                            <p className="text-font-primary" style={{ whiteSpace: 'pre-wrap' }}>{selectedCategory.defaults || 'No defaults available'}</p>
                        </div>
                    )}
                    
                    {activeDropdown === 'typicalUses' && selectedCategory.typicalUses && (
                        <div className="flex flex-col gap-2 card-container rounded-md p-3">
                            <h3 className="font-semibold text-secondary">Typical Uses</h3>
                            <div className="flex flex-wrap gap-2">
                                {selectedCategory.typicalUses}
                            </div>
                        </div>
                    )}
                    
                    {activeDropdown === 'typicalFlow' && (
                        <div className="flex flex-col gap-2 card-container rounded-md p-3">
                            <h3 className="font-semibold text-secondary">Typical Flow</h3>
                            <p className="text-font-primary" style={{ whiteSpace: 'pre-wrap' }}>{selectedCategory.typicalFlow || 'No typical flow available'}</p>
                        </div>
                    )}
                    
                    {activeDropdown === 'qualifyingQuestions' && (
                        <div className="flex flex-col gap-2 card-container rounded-md p-3">
                            <h3 className="font-semibold text-secondary">Qualifying Questions</h3>
                            <p className="text-font-primary" style={{ whiteSpace: 'pre-wrap' }}>{selectedCategory.qualifyingQuestions || 'No qualifying questions available'}</p>
                        </div>
                    )}
                    
                    {activeDropdown === 'specialNotes' && (
                        <div className="flex flex-col gap-2 card-container rounded-md p-3">
                            <h3 className="font-semibold text-secondary">Special Notes</h3>
                            <p className="text-font-primary" style={{ whiteSpace: 'pre-wrap' }}>{selectedCategory.specialNotes || 'No special notes available'}</p>
                        </div>
                    )}
                    
                    {activeDropdown === 'pricingConsiderations' && (
                        <div className="flex flex-col gap-2 card-container rounded-md p-3">
                            <h3 className="font-semibold text-secondary">Pricing Considerations</h3>
                            <p className="text-font-primary" style={{ whiteSpace: 'pre-wrap' }}>{selectedCategory.pricingConsiderations || 'No pricing considerations available'}</p>
                        </div>
                    )}
                </div>
            )}
            {/* Child Items */}
            {selectedCategory.childItems && selectedCategory.childItems.length > 0 ? (
                <div className="flex flex-col gap-2">
                    <div 
                        className={`flex flex-row justify-between items-center cursor-pointer hover:bg-bgSecondary rounded-md p-2 transition-colors ${isEquipmentCollapsed ? 'tag-secondary' : 'tag-gray'}`}
                        onClick={handleEquipmentToggle}
                    >
                        <div className="flex flex-row items-center gap-1">
                            <Icon icon="circum:boxes" className="text-2xl text-accent" />
                            <h3 className="font-semibold text-font-primary text-sm sm:text-base">Equipment</h3>
                        </div>
                        <Icon 
                            icon={isEquipmentCollapsed ? 'mdi:chevron-down' : 'mdi:chevron-up'} 
                            className="text-2xl text-accent" 
                        />
                    </div>
                    {!isEquipmentCollapsed && (
                        <div className="flex flex-col">
                            {selectedCategory.childItems.map((item, itemIndex) => (
                                <div 
                                    key={item.childEqpId || item.childCategory || `${selectedCategory.parentEqpId}_child_${itemIndex}`} 
                                    className="border-b-2 border-bgAccent md:px-3 py-4 flex flex-col gap-3 cursor-pointer hover:bg-bgSecondary transition-colors"
                                    onClick={() => handleChildClick(item)}
                                >
                                    <div className="flex items-center gap-2">
                                        {/* <Icon icon="mdi:package-variant" className={`text-3xl ${
                                            item._hasPendingChanges ? 'text-orange-500 opacity-100' :
                                            item._isPendingCreation ? 'text-primary opacity-100' :
                                            'text-accent opacity-30'
                                        }`} /> */}
                                        <div className="flex-1">
                                            <span className="text-font-primary text-lg">
                                                {item.childCategory || 'Unnamed Item'}
                                            </span>
                                            {item._hasPendingChanges && (
                                                <div className="text-xs text-orange-600 mt-1">
                                                    Modified by {item._changedBy} • {new Date(item._changedAt).toLocaleDateString()}
                                                </div>
                                            )}
                                            {item._isPendingCreation && (
                                                <div className="text-xs text-blue-600 mt-1">
                                                    Created by {item._changedBy} • {new Date(item._changedAt).toLocaleDateString()}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                        {item.model && (
                                            <div className="flex gap-2">
                                                <span className="text-font-secondary">Model:</span>
                                                <span className="text-font-primary">{item.model}</span>
                                            </div>
                                        )}
                                        {item.brand && (
                                            <div className="flex gap-2">
                                                <span className="text-font-secondary">Brand:</span>
                                                <span className="text-font-primary">{item.brand}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                ) : (
                    <div className="card-container p-4 rounded-md text-center">
                        <Icon icon="mdi:package-variant-closed" className="text-3xl text-gray-300 mx-auto mb-2" />
                        <p className="text-font-secondary">No items in this category</p>
                    </div>
                )}


        </div>
    );
};

export default CategoryDetails;
